/**
 * Homepage content that both the React page (src/pages/Index.tsx) and the
 * build-time prerenderer (scripts/prerender.ts) read from.
 *
 * Text and hrefs only — images and icons stay in the page component, keyed by
 * href. Keeping this module asset-free is what lets the Node prerenderer
 * import it.
 */

export interface HomepageFaq { q: string; a: string }

export const homepageFaqs: HomepageFaq[] = [
  { q: "What types of electrical services do you provide in Louisville, KY?", a: "We offer a full range of commercial, residential, and industrial electrical services, including repairs, new installations, system upgrades, preventative maintenance, lighting design, and energy-efficient retrofits." },
  { q: "What industries do you typically work with?", a: "We work with manufacturing facilities, warehouses, office buildings, retail spaces, healthcare facilities, stadiums, schools, hotels, churches, data centers, and more. Our team understands the unique electrical demands of each industry." },
  { q: "Can you help reduce our energy and utility costs?", a: "Absolutely. Our Energy Management Division provides comprehensive energy audits, lighting retrofits, HVAC optimization, and guaranteed energy savings financing. Most businesses save 30-50% on lighting energy costs alone." },
  { q: "Do you offer emergency electrical services?", a: "Yes. Our team is available 24/7 for emergency electrical services, providing rapid response when your business needs it most." },
  { q: "Are you licensed and insured?", a: "Yes. Hadley Energy Solutions is fully licensed (CE64623 | ME64500 | HM06538) and insured. Our technicians maintain current certifications and ongoing safety training." },
  { q: "Do you offer warranties?", a: "Yes! We provide a 1-year warranty on all work performed. Manufacturer's warranties are also available on equipment and materials we install." },
];

/** §3 — the two "fork" cards under the hero. */
export const homepageFork = [
  { title: "For Businesses & Facilities", line: "Commercial, industrial and municipal electrical, HVAC and energy management.", href: "/electrical-services", alt: "Industrial manufacturing floor served by Hadley Energy Solutions in Louisville KY" },
  { title: "For Homeowners", line: "Heating, cooling and electrical service for your home.", href: "/hvac-services/residential-hvac", alt: "Residential HVAC system serviced by Hadley Energy Solutions in Louisville KY" },
];

/** §4 — the four service cards. Mirrors the four homepage Service JSON-LD nodes. */
export const homepageServiceCards = [
  {
    title: "Electrical",
    alt: "Row of commercial switchgear and power distribution cabinets",
    desc: "Fast diagnostics and dependable fixes to minimize downtime. New installations, upgrades, and system expansions.",
    href: "/electrical-services",
    links: [
      { label: "Commercial Electrical Contractor", href: "/electrical-services/commercial-electrical-contractor" },
      { label: "Industrial Electrical", href: "/electrical-services/industrial-electrical-contractor" },
      { label: "Electrical Service & Repair", href: "/electrical-services/electrical-service-and-repair" },
      { label: "24/7 Emergency Electrician", href: "/electrical-services/emergency-electrical-service" },
    ],
  },
  {
    title: "HVAC",
    alt: "Commercial HVAC ductwork installation in a Louisville KY facility",
    desc: "Efficient heating and cooling solutions for your business. 24/7 AC installation, repair, mini-splits & maintenance.",
    href: "/hvac-services",
    links: [
      { label: "Commercial HVAC", href: "/hvac-services/commercial-hvac" },
      { label: "AC Installation & Repair", href: "/hvac-services/ac-installation-repair" },
      { label: "HVAC Installation", href: "/hvac-services/hvac-installation" },
      { label: "Residential HVAC", href: "/hvac-services/residential-hvac" },
    ],
  },
  {
    title: "Energy & Lighting",
    alt: "LED lighting retrofit completed by Hadley Energy Solutions",
    desc: "Reduce energy costs with upgraded lighting systems. Identify inefficiencies and uncover cost-saving opportunities.",
    href: "/energy-management",
    links: [
      { label: "Lighting Retrofits & LED", href: "/energy-management/lighting-retrofits" },
      { label: "Energy Audits", href: "/energy-management/energy-audits" },
      { label: "Energy Bill Analysis", href: "/energy-management/energy-bill-analysis" },
      { label: "Guaranteed Energy Savings", href: "/energy-management/guaranteed-energy-savings" },
    ],
  },
  {
    title: "Stadium & Sports Lighting",
    alt: "LED football stadium lighting installed by Hadley Energy Solutions",
    desc: "Professional sports field and arena lighting systems built for performance and broadcast-quality illumination.",
    href: "/stadium-sports-lighting",
    links: [
      { label: "Football Stadium Lighting", href: "/stadium-sports-lighting/football-stadium-lighting" },
      { label: "Baseball Field Lighting", href: "/stadium-sports-lighting/baseball-field-lighting" },
      { label: "Tennis Court Lighting", href: "/stadium-sports-lighting/tennis-court-lighting" },
    ],
  },
];

/** §7 — industries grid (icons live in the page component, keyed by href). */
export const homepageIndustries = [
  { title: "Manufacturing & Automotive", desc: "Mission-critical electrical systems for Louisville's industrial facilities.", href: "/industries/manufacturing-electrical" },
  { title: "Stadiums & Sports Venues", desc: "Professional-grade stadium lighting from high school to pro venues.", href: "/stadium-sports-lighting" },
  { title: "Healthcare & Hospitals", desc: "Compliance-focused electrical and HVAC with zero-downtime requirements.", href: "/industries/healthcare-facility-compliance" },
  { title: "Warehousing & Distribution", desc: "Energy-efficient lighting and power for logistics facilities.", href: "/industries/warehouse-electrical" },
  { title: "Universities & Education", desc: "Campus lighting, lab electrical, and building automation.", href: "/industries/school-electrical-contractor" },
  { title: "Hotels & Commercial", desc: "Full-service electrical and HVAC for hospitality and retail.", href: "/industries/hotel-electrical-contractor" },
  { title: "Government & Municipal", desc: "Water treatment, public infrastructure, and municipal projects.", href: "/industries/municipal-electrical" },
  { title: "Residential HVAC", desc: "Professional heating, cooling, and ductwork for homeowners.", href: "/hvac-services/residential-hvac" },
];

/** §10 — priority service-area links. */
export const homepageServiceAreas: { name: string; slug: string }[] = [
  { name: "Louisville", slug: "louisville-ky" },
  { name: "Elizabethtown", slug: "elizabethtown-ky" },
  { name: "Lexington", slug: "lexington-ky" },
  { name: "Frankfort", slug: "frankfort-ky" },
  { name: "Bowling Green", slug: "bowling-green-ky" },
  { name: "Shepherdsville", slug: "shepherdsville-ky" },
  { name: "Mt. Washington", slug: "mt-washington-ky" },
  { name: "Jeffersontown", slug: "jeffersontown-ky" },
  { name: "St. Matthews", slug: "st-matthews-ky" },
  { name: "Bardstown", slug: "bardstown-ky" },
  { name: "La Grange", slug: "la-grange-ky" },
  { name: "New Albany, IN", slug: "new-albany-in" },
  { name: "Jeffersonville, IN", slug: "jeffersonville-in" },
];

/** Utility / conversion pages linked from the homepage body. */
export const homepageUtilityLinks = [
  { label: "About Hadley Energy Solutions", href: "/about" },
  { label: "Portfolio", href: "/portfolio" },
  { label: "Contact", href: "/contact" },
  { label: "Reviews", href: "/reviews" },
  { label: "Request an Estimate", href: "/request-estimate" },
];

/** Inputs for the four homepage Service JSON-LD nodes. */
export const homepageServiceSchemaInputs = [
  { name: "Commercial Electrical Contracting", description: "Licensed commercial electrical contractor in Louisville, KY — service, repair, panel upgrades, generators, EV chargers, and 24/7 emergency response.", url: "/electrical-services", category: "Electrical Services" },
  { name: "HVAC Installation & Repair", description: "Licensed HVAC contractor in Louisville, KY — AC, heating, mini-splits, commercial RTUs, smart thermostats, and 24/7 emergency repair. HVAC Master HM06538.", url: "/hvac-services", category: "HVAC Services" },
  { name: "Energy Management & LED Lighting", description: "Energy audits, LED lighting retrofits, lighting controls, and ESCO financing for Louisville commercial and industrial facilities.", url: "/energy-management", category: "Energy Management" },
  { name: "Stadium & Sports Lighting", description: "Broadcast-quality stadium, field, court, and arena lighting design and installation across Kentucky and Southern Indiana.", url: "/stadium-sports-lighting", category: "Stadium Lighting" },
];
